import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

const MATCH_BADGES = {
  full_match: 'bg-success',
  partial_match: 'bg-info text-dark',
  amount_mismatch: 'bg-warning text-dark',
  tax_mismatch: 'bg-warning text-dark',
  no_sage_match: 'bg-danger',
  no_po_match: 'bg-danger',
  unmatched_supplier: 'bg-danger',
  duplicate: 'bg-secondary',
  credit_note: 'bg-primary',
};

function fmt(val) {
  if (val == null) return '–';
  return `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}


export default function Reconciliation() {
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [unmatchedInv, setUnmatchedInv] = useState([]);
  const [unmatchedSage, setUnmatchedSage] = useState([]);
  const [activeTab, setActiveTab] = useState('main');
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pageMain, setPageMain] = useState(1);
  const [pageInv, setPageInv] = useState(1);
  const [pageSage, setPageSage] = useState(1);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  const fetchAll = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    Promise.all([
      client.get('/reconciliation/', { params }),
      client.get('/reconciliation/stats'),
      client.get('/reconciliation/unmatched-invoices', { params }),
      client.get('/reconciliation/unmatched-sage', { params }),
    ])
      .then(([r, st, ui, us]) => {
        setResults(r.data.results || []);
        setStats(st.data);
        setUnmatchedInv(ui.data.invoices || []);
        setUnmatchedSage(us.data.creditors || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPageMain(1); setPageInv(1); setPageSage(1);
  };

  const paginate = (data, page) => {
    const total = data.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const current = Math.min(page, totalPages);
    return { paginated: data.slice((current - 1) * pageSize, current * pageSize), total, totalPages, current };
  };

  const main = paginate(results, pageMain);
  const inv = paginate(unmatchedInv, pageInv);
  const sage = paginate(unmatchedSage, pageSage);

  return (
    <div>
      {/* Stats cards */}
      {stats && (
        <div className="row g-3 mb-0">
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #198754' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#198754' }}>{stats.full_match}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Full Match</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #0dcaf0' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#0dcaf0' }}>{stats.partial_match}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Partial</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #ffc107' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#ffc107' }}>{stats.amount_mismatch}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Amount Mismatch</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #dc3545' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#dc3545' }}>{stats.unmatched_supplier}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Unmatched Supplier</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #fd7e14' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#fd7e14' }}>{unmatchedInv.length}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Unmatched Invoices</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card text-center" style={{ borderLeft: '4px solid #6c757d' }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#6c757d' }}>{unmatchedSage.length}</div>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Unmatched Sage</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Period filter */}
      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPageMain(1); setPageInv(1); setPageSage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPageMain(1); setPageInv(1); setPageSage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPageMain(1); setPageInv(1); setPageSage(1); }}
      />

      {/* Tabs */}
      <ul className="nav app-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link${activeTab === 'main' ? ' active' : ''}`} onClick={() => setActiveTab('main')}>
            Reconciliation Results ({results.length})
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link${activeTab === 'inv' ? ' active' : ''}`} onClick={() => setActiveTab('inv')}>
            Unmatched Invoices ({unmatchedInv.length})
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link${activeTab === 'sage' ? ' active' : ''}`} onClick={() => setActiveTab('sage')}>
            Sage Without Invoice ({unmatchedSage.length})
          </button>
        </li>
      </ul>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
      ) : (
        <>
          {activeTab === 'main' && (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Invoice Supplier</th>
                        <th>Invoice #</th>
                        <th className="text-end">Invoice Amount</th>
                        <th>PO #</th>
                        <th>Sage A/C</th>
                        <th className="text-end">Sage Balance</th>
                        <th>Match Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {main.paginated.length === 0 && (
                        <tr><td colSpan="7" className="text-center text-muted py-4">No reconciliation data. Run the pipeline first.</td></tr>
                      )}
                      {main.paginated.map(r => (
                        <tr key={r.id}>
                          <td>{r.supplier_canonical_name || r.supplier_name || '–'}</td>
                          <td>{r.invoice_number || '–'}</td>
                          <td className="text-end">{fmt(r.gross_amount)}</td>
                          <td>{r.po_number || '–'}</td>
                          <td>{r.sage_account || r.sage_account_code || '–'}</td>
                          <td className="text-end">{fmt(r.sage_balance)}</td>
                          <td>
                            <span className={`badge ${MATCH_BADGES[r.match_type] || 'bg-secondary'}`}>
                              {r.match_type?.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {results.length > 0 && (
                <PaginationFooter page={main.current} totalPages={main.totalPages} total={main.total} pageSize={pageSize} onPageChange={setPageMain} onPageSizeChange={handlePageSizeChange} />
              )}
            </div>
          )}

          {activeTab === 'inv' && (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Supplier</th>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th className="text-end">Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inv.paginated.length === 0 && (
                        <tr><td colSpan="5" className="text-center text-muted py-4">No unmatched invoices</td></tr>
                      )}
                      {inv.paginated.map(i => (
                        <tr key={i.id}>
                          <td>{i.matched_supplier_name || i.supplier_name || '–'}</td>
                          <td>{i.invoice_number || '–'}</td>
                          <td>{i.invoice_date || '–'}</td>
                          <td className="text-end">{fmt(i.gross_amount)}</td>
                          <td><span className="badge bg-secondary">{i.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {unmatchedInv.length > 0 && (
                <PaginationFooter page={inv.current} totalPages={inv.totalPages} total={inv.total} pageSize={pageSize} onPageChange={setPageInv} onPageSizeChange={handlePageSizeChange} />
              )}
            </div>
          )}

          {activeTab === 'sage' && (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Account Code</th>
                        <th>Supplier Name</th>
                        <th className="text-end">Balance</th>
                        <th className="text-end">Current</th>
                        <th className="text-end">30 Days</th>
                        <th className="text-end">60 Days</th>
                        <th>Import Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sage.paginated.length === 0 && (
                        <tr><td colSpan="7" className="text-center text-muted py-4">All Sage creditors are matched</td></tr>
                      )}
                      {sage.paginated.map(sc => (
                        <tr key={sc.id}>
                          <td>{sc.account_code || '–'}</td>
                          <td>{sc.supplier_name || '–'}</td>
                          <td className="text-end">{fmt(sc.balance)}</td>
                          <td className="text-end">{fmt(sc.current_amount)}</td>
                          <td className="text-end">{fmt(sc.period1_amount)}</td>
                          <td className="text-end">{fmt(sc.period2_amount)}</td>
                          <td>{sc.import_date ? new Date(sc.import_date).toLocaleDateString('en-GB') : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {unmatchedSage.length > 0 && (
                <PaginationFooter page={sage.current} totalPages={sage.totalPages} total={sage.total} pageSize={pageSize} onPageChange={setPageSage} onPageSizeChange={handlePageSizeChange} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
